import i18n.LocaleManager
import i18n.StringKey
import i18n.tr
import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.HTMLElement

class GuidePage(
    private val onBackClick: () -> Unit,
    private val onNavigate: ((String) -> Unit)? = null,
    private val isLoggedIn: Boolean = false,
    private val onAppNavigate: ((String) -> Unit)? = null,
    private val asModal: Boolean = false,
) {
    private val isMobile = ResponsiveUtils.isMobile()

    fun render(): HTMLElement {
        val container = document.createElement("div") as HTMLElement
        val t = WebTheme.current
        val pageBg = if (t.isDark) "transparent" else t.background
        val cardBg = if (t.isDark) "#141418" else t.card
        val cardBorder = if (t.isDark) "#1f1f1f" else t.border
        val textMain = if (t.isDark) "#ffffff" else t.textPrimary
        val textMuted = if (t.isDark) "#99a0a6" else t.textSecondary
        val footerBg = if (t.isDark) "#0f0f0f" else t.card
        val footerBorder = if (t.isDark) "#1a1a1a" else t.border
        val footerText = if (t.isDark) "#666" else t.textSecondary
        val proCardBg = if (t.isDark) "#0d1b2a" else "${t.accent}12"
        val proCardBorder = if (t.isDark) "#1b3a5c" else "${t.accent}40"
        val proTitle = if (t.isDark) "#00d9ff" else t.accentStrong
        val accentColor = if (t.isDark) "#00d9ff" else t.accentStrong
        val ctaBg = if (t.isDark) "linear-gradient(135deg, #00d9ff 0%, #00b8d9 100%)" else "linear-gradient(135deg, ${t.accentStrong} 0%, #006064 100%)"
        val ctaText = if (t.isDark) "#0a0a0a" else "#ffffff"
        container.style.cssText = if (asModal) """
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            background: transparent; color: $textMain;
        """ else """
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            background: $pageBg; color: $textMain; min-height: 100vh;
        """

        val backLinkColor = if (t.isDark) "#00d9ff" else t.accentStrong
        val backText = if (LocaleManager.getCurrentLocale().code == "fr") "‹ Retour" else "‹ Back"
        val maxWidth = if (asModal) "100%" else if (isMobile) "100%" else "720px"
        val contentPadding = if (asModal) "32px 28px 0" else if (isMobile) "60px 20px 0" else "80px 48px 0"
        val titlePaddingLeft = if (asModal) "0" else if (isMobile) "0" else "440px"

        val barsOpacity = if (t.isDark) "" else "opacity: 0.3; filter: brightness(1.5);"
        // Modal skips full-page chrome: bars animation, fixed nav, and the footer/CTA.
        val barsHtml = if (asModal || isMobile) "" else """
                <!-- 3-bars animated container -->
                <div id="bars-container" style="position: fixed; left: 0; top: 10px; z-index: 1; pointer-events: none; transition: none; $barsOpacity">
                    <img id="bars-3" src="/team-3-bars.png" style="position: absolute; left: 0; top: 0; height: 450px; opacity: 0;">
                    <img id="bars-frame" src="/team-bars-wizer-frame.png" style="position: absolute; left: 0; top: 0; height: 450px; opacity: 1;">
                    <img id="bars-wizer" src="/team-wizer-only.png" style="position: absolute; left: 0; top: 0; height: 450px; opacity: 1;">
                </div>
        """

        val navHtml = when {
            asModal -> ""
            !isMobile && onNavigate != null ->
                MarketingMenu.hamburgerButtonHtml() + MarketingMenu.overlayHtml(currentPage = "guide", isLoggedIn = isLoggedIn)
            else -> """<div style="position: fixed; top: 20px; right: 24px; z-index: 100;">
                <a id="guide-back" href="#" style="color: $backLinkColor; text-decoration: none; font-size: 15px; font-weight: 500; cursor: pointer;">$backText</a>
            </div>"""
        }

        val ambientGlow = if (!asModal && t.isDark) """
            <div style="position: fixed; top: 0; left: 0; right: 0; height: 56px; background: transparent; z-index: 0; pointer-events: none;"></div>
        """ else ""

        container.innerHTML = """
            $ambientGlow
            $navHtml

            $barsHtml

            <!-- Content -->
            <div style="position: relative; z-index: 2; padding: $contentPadding; padding-right: 80px;">
                <div style="padding-left: $titlePaddingLeft;">
                    <div style="max-width: $maxWidth;">
                        <h1 style="font-size: ${if (isMobile) "28px" else "36px"}; margin-bottom: 8px;">
                            ${StringKey.GUIDE_TITLE.tr()}
                        </h1>
                        <p style="color: $textMuted; font-size: 16px; margin-bottom: 40px;">
                            ${StringKey.GUIDE_SUBTITLE.tr()}
                        </p>

                        <!-- Step 1 -->
                        <div style="background: $cardBg; border-radius: 16px; padding: 24px; margin-bottom: 20px; border: 1px solid $cardBorder; box-shadow: ${t.cardShadow};">
                            <div style="display: flex; align-items: center; gap: 16px; margin-bottom: 12px;">
                                <div style="
                                    width: 40px; height: 40px; border-radius: 50%; background: ${accentColor}20;
                                    display: flex; align-items: center; justify-content: center;
                                    font-size: 20px; font-weight: 700; color: $accentColor;
                                ">1</div>
                                <h2 style="margin: 0; font-size: 20px; color: $textMain;">${StringKey.GUIDE_STEP1_TITLE.tr()}</h2>
                            </div>
                            <p style="color: ${if (t.isDark) textMuted else t.textSecondary}; line-height: 1.6; margin: 0;">
                                ${StringKey.GUIDE_STEP1_DESC.tr()}
                            </p>
                        </div>

                        <!-- Step 2 -->
                        <div style="background: $cardBg; border-radius: 16px; padding: 24px; margin-bottom: 20px; border: 1px solid $cardBorder; box-shadow: ${t.cardShadow};">
                            <div style="display: flex; align-items: center; gap: 16px; margin-bottom: 12px;">
                                <div style="
                                    width: 40px; height: 40px; border-radius: 50%; background: #22C55E20;
                                    display: flex; align-items: center; justify-content: center;
                                    font-size: 20px; font-weight: 700; color: #22C55E;
                                ">2</div>
                                <h2 style="margin: 0; font-size: 20px; color: $textMain;">${StringKey.GUIDE_STEP2_TITLE.tr()}</h2>
                            </div>
                            <p style="color: ${if (t.isDark) textMuted else t.textSecondary}; line-height: 1.6; margin: 0;">
                                ${StringKey.GUIDE_STEP2_DESC.tr()}
                            </p>
                        </div>

                        <!-- Step 3 -->
                        <div style="background: $cardBg; border-radius: 16px; padding: 24px; margin-bottom: 20px; border: 1px solid $cardBorder; box-shadow: ${t.cardShadow};">
                            <div style="display: flex; align-items: center; gap: 16px; margin-bottom: 12px;">
                                <div style="
                                    width: 40px; height: 40px; border-radius: 50%; background: #8B5CF620;
                                    display: flex; align-items: center; justify-content: center;
                                    font-size: 20px; font-weight: 700; color: #8B5CF6;
                                ">3</div>
                                <h2 style="margin: 0; font-size: 20px; color: $textMain;">${StringKey.GUIDE_STEP3_TITLE.tr()}</h2>
                            </div>
                            <p style="color: ${if (t.isDark) textMuted else t.textSecondary}; line-height: 1.6; margin: 0;">
                                ${StringKey.GUIDE_STEP3_DESC.tr()}
                            </p>
                        </div>

                        <!-- Step 4 -->
                        <div style="background: $cardBg; border-radius: 16px; padding: 24px; margin-bottom: 20px; border: 1px solid $cardBorder; box-shadow: ${t.cardShadow};">
                            <div style="display: flex; align-items: center; gap: 16px; margin-bottom: 12px;">
                                <div style="
                                    width: 40px; height: 40px; border-radius: 50%; background: #F59E0B20;
                                    display: flex; align-items: center; justify-content: center;
                                    font-size: 20px; font-weight: 700; color: #F59E0B;
                                ">4</div>
                                <h2 style="margin: 0; font-size: 20px; color: $textMain;">${StringKey.GUIDE_STEP4_TITLE.tr()}</h2>
                            </div>
                            <p style="color: ${if (t.isDark) textMuted else t.textSecondary}; line-height: 1.6; margin: 0;">
                                ${StringKey.GUIDE_STEP4_DESC.tr()}
                            </p>
                        </div>

                        <!-- Pro Tips -->
                        <div style="background: $proCardBg; border-radius: 16px; padding: 24px; margin-bottom: 20px; border: 1px solid $proCardBorder;">
                            <h2 style="margin: 0 0 16px; font-size: 20px; color: $proTitle;">${StringKey.GUIDE_PRO_TIPS.tr()}</h2>
                            <ul style="color: ${if (t.isDark) textMuted else t.textSecondary}; line-height: 1.8; margin: 0; padding-left: 20px;">
                                <li>${StringKey.GUIDE_TIP_TRACK.tr()}</li>
                                <li>${StringKey.GUIDE_TIP_ONE_GOAL.tr()}</li>
                                <li>${StringKey.GUIDE_TIP_CHECK_IN.tr()}</li>
                                <li>${StringKey.GUIDE_TIP_BUDGET.tr()}</li>
                                <li>${StringKey.GUIDE_TIP_AI.tr()}</li>
                            </ul>
                        </div>

                        ${if (asModal) "" else """
                        <!-- CTA -->
                        <div style="text-align: center; padding: 32px 0;">
                            <p style="color: $textMuted; font-size: 16px; margin-bottom: 20px;">
                                ${StringKey.GUIDE_CTA.tr()}
                            </p>
                            <button id="guide-get-started" style="
                                background: $ctaBg;
                                color: $ctaText; border: none; border-radius: 8px;
                                padding: 14px 32px; font-size: 16px; font-weight: 700;
                                cursor: pointer; box-shadow: 0 4px 12px rgba(0, 217, 255, 0.3);
                            ">${StringKey.LANDING_GET_STARTED.tr()}</button>
                        </div>
                        """}
                    </div>
                </div>
            </div>

            ${if (asModal) "" else MarketingFooter.html(currentPage = "guide")}
        """

        return container
    }

    fun attachEventListeners(element: HTMLElement) {
        window.setTimeout({
            if (asModal) {
                // No chrome to wire up; dismiss is handled by the surrounding modal.
                return@setTimeout
            }
            if (!isMobile && onNavigate != null) {
                MarketingMenu.attachEventListeners(
                    onNavigate = onNavigate,
                    isLoggedIn = isLoggedIn,
                    onAppNavigate = onAppNavigate,
                )
            } else {
                element.querySelector("#guide-back")?.addEventListener("click", { onBackClick() })
            }
            onNavigate?.let { MarketingFooter.attachEventListeners(it) }
            element.querySelector("#guide-get-started")?.addEventListener("click", { onBackClick() })
            setupBarsAnimation()
        }, 100)
    }

    private fun setupBarsAnimation() {
        if (asModal || isMobile) return
        val barsContainer = document.getElementById("bars-container") as? HTMLElement ?: return
        val bars3 = document.getElementById("bars-3") as? HTMLElement ?: return
        val barsFrame = document.getElementById("bars-frame") as? HTMLElement ?: return
        val barsWizer = document.getElementById("bars-wizer") as? HTMLElement ?: return

        val startScale = 0.35
        val endScale = 1.0
        val startTop = 10.0
        val endTop = window.innerHeight / 2.0 - 225

        barsContainer.style.top = "${startTop.toInt()}px"
        barsContainer.style.transform = "scale($startScale)"
        barsContainer.style.transformOrigin = "left top"

        bars3.style.opacity = "0"
        barsFrame.style.opacity = "1"
        barsWizer.style.opacity = "1"

        window.addEventListener("scroll", {
            val scrollY = window.scrollY
            val growScroll = 600.0
            val progress = (scrollY / growScroll).coerceIn(0.0, 1.0)

            val scale = startScale + (endScale - startScale) * progress
            val currentTop = startTop + (endTop - startTop) * progress
            barsContainer.style.transform = "scale($scale)"
            barsContainer.style.top = "${currentTop.toInt()}px"
            barsContainer.style.transformOrigin = "left top"

            barsWizer.style.opacity = "${(1.0 - progress * 1.4).coerceIn(0.0, 1.0)}"
            barsFrame.style.opacity = "${(1.0 - progress * 1.2).coerceIn(0.0, 1.0)}"
            bars3.style.opacity = "${((progress - 0.2) * 1.5).coerceIn(0.0, 1.0)}"

            if (scrollY > growScroll) {
                val extraScroll = scrollY - growScroll
                val drift = extraScroll * 0.15
                val maxTop = window.innerHeight - 450.0 - 50.0
                val cappedTop = (endTop + drift).coerceAtMost(maxTop)
                barsContainer.style.top = "${cappedTop.toInt()}px"
            }
        })
    }
}
